import React, { useMemo } from 'react';
import { Stack, Text, Group, Badge, ScrollArea, Box, Progress, Tooltip, Divider, useMantineColorScheme } from '@mantine/core';
import { Target, List, Hash, Users } from 'lucide-react';
import { getCompactRowStyle } from './compactRowStyles';
import { useDashboardData } from '../../../../hooks/dashboard/useDashboardData';
import { useNavigation } from '../../../../contexts/NavigationContext';
import { useIsTouch } from './useIsTouch';
import { useIsSmallScreen } from './useIsSmallScreen';

interface FocusAreasProps {
  className?: string;
}

interface FocusArea {
  id: string;
  name: string;
  type: 'list' | 'tag' | 'priority' | 'assignee';
  count: number;
  completionRate: number;
  recentActivity: number;
  urgentTasks: number;
  icon: React.ReactNode;
  color: string;
  description: string;
}

export const FocusAreas: React.FC<FocusAreasProps> = ({ className }) => {
  const dashboardData = useDashboardData();
  const isTouch = useIsTouch();
  const isSmall = useIsSmallScreen();
  const showTooltips = !(isTouch && isSmall);
  const { colorScheme } = useMantineColorScheme();
  useNavigation(); // kept for potential side-effects

  // Generate focus areas with comprehensive analysis
  const focusAreas = useMemo(() => {
    const areas: FocusArea[] = [];

    // Most Active Lists (by task count and recent activity)
    dashboardData.mostActiveLists.slice(0, 4).forEach(listData => {
      const { list, taskCount, completionRate } = listData;
      const urgentTasks = dashboardData.todos.filter(t =>
        t.listId === list.id &&
        !t.isCompleted &&
        (t.priority === 'High' || (t.dueDate && new Date(t.dueDate) <= new Date()))
      ).length;

      areas.push({
        id: `list-${list.id}`,
        name: list.name,
        type: 'list',
        count: taskCount,
        completionRate,
  recentActivity: 0,
        urgentTasks,
        icon: <List size={16} />,
        color: list.color || 'blue',
        description: `${taskCount} tasks • ${completionRate}% complete`
      });
    });

    // High Priority Focus Area (if significant)
    if (dashboardData.highPriorityTasks >= 3) {
      const highPriorityTodos = dashboardData.todos.filter(t => t.priority === 'High' && !t.isCompleted);
      const overdueHighPriority = highPriorityTodos.filter(t => 
        t.dueDate && new Date(t.dueDate) < new Date()
      ).length;
      
      areas.push({
        id: 'priority-high',
        name: 'High Priority',
        type: 'priority',
        count: dashboardData.highPriorityTasks,
        completionRate: 0, // Not applicable for priority grouping
        recentActivity: dashboardData.recentlyCompletedTasks.filter(t => t.priority === 'High').length,
        urgentTasks: overdueHighPriority,
        icon: <Target size={16} />,
        color: 'red',
        description: `${dashboardData.highPriorityTasks} high-priority tasks`
      });
    }

    // Tag-based focus areas (if tags are used)
    const tagUsage = new Map<string, { count: number; completed: number; recent: number }>();
    dashboardData.todos.forEach(todo => {
      // Normalize tags to a string array; tolerate legacy/string/null shapes to avoid runtime errors
      const rawTags = (todo as any).tags; // fallback to any to permit legacy shapes
      let normalizedTags: string[] = [];
      if (Array.isArray(rawTags)) {
        normalizedTags = rawTags.filter(t => typeof t === 'string');
      } else if (typeof rawTags === 'string') {
        const trimmed = rawTags.trim();
        if (trimmed) normalizedTags = [trimmed];
      }

      if (normalizedTags.length === 0) return;

      normalizedTags.forEach(tag => {
        const current = tagUsage.get(tag) || { count: 0, completed: 0, recent: 0 };
        current.count++;
        if (todo.isCompleted) current.completed++;
        if (dashboardData.recentlyCompletedTasks.some(rt => rt.id === todo.id)) {
          current.recent++;
        }
        tagUsage.set(tag, current);
      });
    });

    // Add top 2 tags as focus areas
    Array.from(tagUsage.entries())
      .sort(([,a], [,b]) => b.count - a.count)
      .slice(0, 2)
      .forEach(([tag, data]) => {
        if (data.count >= 3) { // Only show tags with significant usage
          const completionRate = Math.round((data.completed / data.count) * 100);
          areas.push({
            id: `tag-${tag}`,
            name: `#${tag}`,
            type: 'tag',
            count: data.count,
            completionRate,
            recentActivity: data.recent,
            urgentTasks: 0, // Tags don't have urgency
            icon: <Hash size={16} />,
            color: 'cyan',
            description: `${data.count} tagged tasks • ${completionRate}% complete`
          });
        }
      });

    // Assignee focus areas (if tasks are assigned)
    const assigneeUsage = new Map<string, { count: number; completed: number; recent: number }>();
    dashboardData.todos.forEach(todo => {
      if (todo.assignedTo) {
        const current = assigneeUsage.get(todo.assignedTo) || { count: 0, completed: 0, recent: 0 };
        current.count++;
        if (todo.isCompleted) current.completed++;
        if (dashboardData.recentlyCompletedTasks.some(rt => rt.id === todo.id)) {
          current.recent++;
        }
        assigneeUsage.set(todo.assignedTo, current);
      }
    });

    // Add top assignee as focus area
    const topAssignee = Array.from(assigneeUsage.entries())
      .sort(([,a], [,b]) => b.count - a.count)[0];
    
    if (topAssignee && topAssignee[1].count >= 3) {
      const [assignee, data] = topAssignee;
      const completionRate = Math.round((data.completed / data.count) * 100);
      areas.push({
        id: `assignee-${assignee}`,
        name: assignee.split('@')[0], // Just the name part of email
        type: 'assignee',
        count: data.count,
        completionRate,
        recentActivity: data.recent,
        urgentTasks: 0,
        icon: <Users size={16} />,
        color: 'purple',
        description: `${data.count} assigned tasks • ${completionRate}% complete`
      });
    }

    // Sort by activity and importance
    return areas
      .sort((a, b) => {
        // Prioritize areas with urgent tasks
        if (a.urgentTasks !== b.urgentTasks) {
          return b.urgentTasks - a.urgentTasks;
        }
        // Then by recent activity
        if (a.recentActivity !== b.recentActivity) {
          return b.recentActivity - a.recentActivity;
        }
        // Finally by total task count
        return b.count - a.count;
      })
      .slice(0, 6); // Top 6 focus areas
  }, [dashboardData]);

  // Navigation removed in simplified version

  // Activity/urgency helper functions removed in simplified view

  if (focusAreas.length === 0) {
    return (
      <Stack gap="xs" className={className}>
        <Group gap={6}>
          <Target size={16} style={{ color: 'var(--mantine-color-blue-6)' }} />
          <Text size="sm" fw={600}>Focus Areas</Text>
        </Group>
        <Text size="xs" c="dimmed">Not enough data yet</Text>
      </Stack>
    );
  }

  return (
    <Stack gap="xs" className={className}>
      <Group gap={6}>
        <Target size={16} style={{ color: 'var(--mantine-color-blue-6)' }} />
        <Text size="sm" fw={600}>Focus Areas</Text>
        {showTooltips ? (
          <Tooltip label="Number of focus areas" withArrow>
            <Badge size="xs" variant="light" color="blue">{focusAreas.length}</Badge>
          </Tooltip>
        ) : (
          <Badge size="xs" variant="light" color="blue">{focusAreas.length}</Badge>
        )}
      </Group>
  <ScrollArea h={210} offsetScrollbars>
        <Stack gap={4}>
          {focusAreas.slice(0,5).map(area => {
            const accent = area.color || 'blue';
            const isDark = colorScheme === 'dark';
            return (
              <div
                key={area.id}
                className="focus-area-row"
                style={{
                  // Base compact row shell (neutral, high contrast)
                  ...getCompactRowStyle(),
                  display: 'grid',
                  gridTemplateColumns: '22px 1fr auto',
                  alignItems: 'center',
                  gap: 6,
                  background: isDark
                    ? 'var(--mantine-color-dark-6)'
                    : 'var(--mantine-color-gray-0)',
                  border: isDark
                    ? '1px solid var(--mantine-color-dark-4)'
                    : '1px solid var(--mantine-color-gray-3)',
                  color: isDark
                    ? 'var(--mantine-color-gray-1)'
                    : 'var(--mantine-color-dark-7)'
                }}
              >
                <div
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: isDark
                      ? `var(--mantine-color-${accent}-6)`
                      : `var(--mantine-color-${accent}-1)`,
                    border: isDark
                      ? `1px solid var(--mantine-color-${accent}-4)`
                      : `1px solid var(--mantine-color-${accent}-3)`,
                    color: isDark
                      ? 'var(--mantine-color-gray-0)'
                      : `var(--mantine-color-${accent}-7)`
                  }}
                >
                  {area.icon}
                </div>
                <Box style={{ minWidth:0 }}>
                  <Group gap={4} wrap="nowrap" justify="space-between" style={{ width:'100%' }}>
                    <Text size="xs" fw={500} lineClamp={1} style={{ flex:1 }}>{area.name}</Text>
                    {area.type === 'list' && (
                      showTooltips ? (
                        <Tooltip label="Total tasks in list" withArrow>
                          <Badge size="xs" variant="outline" radius="sm" color="green">{area.count}</Badge>
                        </Tooltip>
                      ) : (
                        <Badge size="xs" variant="outline" radius="sm" color="green">{area.count}</Badge>
                      )
                    )}
                  </Group>
                  <Group gap={4} wrap="nowrap" mt={2} align="center">
                    {area.type === 'list' && area.completionRate > 0 && (
                      showTooltips ? (
                        <Tooltip label={`Completion ${area.completionRate}%`} withArrow>
                          <Progress value={area.completionRate} size="xs" style={{ flex:1 }} color={area.completionRate >= 80 ? 'green' : area.completionRate >= 50 ? 'blue' : 'orange'} />
                        </Tooltip>
                      ) : (
                        <Progress value={area.completionRate} size="xs" style={{ flex:1 }} color={area.completionRate >= 80 ? 'green' : area.completionRate >= 50 ? 'blue' : 'orange'} />
                      )
                    )}
                    {area.urgentTasks > 0 && (
                      showTooltips ? (
                        <Tooltip label={`${area.urgentTasks} urgent task${area.urgentTasks===1?'':'s'}`} withArrow>
                          <Badge size="xs" variant="filled" radius="sm" color={area.urgentTasks >=3 ? 'red':'orange'}>{area.urgentTasks}</Badge>
                        </Tooltip>
                      ) : (
                        <Badge size="xs" variant="filled" radius="sm" color={area.urgentTasks >=3 ? 'red':'orange'}>{area.urgentTasks}</Badge>
                      )
                    )}
                    {area.recentActivity > 0 && (
                      showTooltips ? (
                        <Tooltip label={`${area.recentActivity} recent completion${area.recentActivity===1?'':'s'}`} withArrow>
                          <Badge size="xs" variant="light" radius="sm" color="blue">{area.recentActivity}r</Badge>
                        </Tooltip>
                      ) : (
                        <Badge size="xs" variant="light" radius="sm" color="blue">{area.recentActivity}r</Badge>
                      )
                    )}
                  </Group>
                </Box>
                {/* placeholder for alignment if needed */}
                <div style={{ width:0, height:0 }} />
              </div>
            );
          })}
        </Stack>
      </ScrollArea>
      {isTouch && isSmall && (
        <>
          <Divider my={4} />
          <Group gap={4} wrap="wrap" style={{ fontSize:10, lineHeight:1.2, alignItems:'center' }} c="dimmed">
            <Text size="10px" fw={500}>Legend:</Text>
            <Group gap={4} style={{ alignItems:'center' }}>
              <span style={{ width:10, height:10, background:'var(--mantine-color-green-6)', borderRadius:2, display:'inline-block' }} />
              <Text size="10px">Count</Text>
            </Group>
            <Group gap={4} style={{ alignItems:'center' }}>
              <span style={{ width:10, height:10, background:'linear-gradient(90deg,var(--mantine-color-blue-5),var(--mantine-color-green-5))', borderRadius:2, display:'inline-block' }} />
              <Text size="10px">Completion</Text>
            </Group>
            <Group gap={4} style={{ alignItems:'center' }}>
              <span style={{ width:10, height:10, background:'var(--mantine-color-red-6)', borderRadius:2, display:'inline-block' }} />
              <Text size="10px">Urgent</Text>
            </Group>
            <Group gap={4} style={{ alignItems:'center' }}>
              <span style={{ width:10, height:10, background:'var(--mantine-color-blue-6)', borderRadius:2, display:'inline-block' }} />
              <Text size="10px">Recent</Text>
            </Group>
          </Group>
        </>
      )}
    </Stack>
  );
};